import React, {
  FocusEventHandler,
  HTMLInputAutoCompleteAttribute,
  useState
} from "react";
import { InputBaseProps } from "..";
import { SanityInputTextProps } from "../../../models/form/SanityFormInputField";
import { isNullOrWhiteSpace } from "../../../shared/string";
import { autoCompleteSupportedFields } from "../helpers";
import { useFieldValidator } from "../Validation";
import InputContainer from "./InputContainer";
import { useFieldCustom } from "./useFieldCustom";

export type TextAreaProps = InputBaseProps<SanityInputTextProps> & {
  autoComplete?: HTMLInputAutoCompleteAttribute;
};

const TextArea = (props: TextAreaProps) => {
  const { autoComplete, name, label, placeholder, readonly, validation } = props;

  const validator = useFieldValidator(validation);
  const [field, meta] = useFieldCustom({
    name,
    validate: validator
  });

  const [focused, setFocused] = useState(false);

  const onFocus: FocusEventHandler<HTMLTextAreaElement> = () => {
    setFocused(true);
  };

  const onBlur: FocusEventHandler<HTMLTextAreaElement> = e => {
    field.onBlur(e);
    setFocused(false);
  };

  let containerClasses = "w-full rounded border";
  let inputClasses =
    "block w-full h-32 px-4 pb-3 outline-none text-brand-primary-20 text-lg appearance-none";
  let labelClasses =
    "block mt-3 mx-4 text-lg transform transition-all origin-top-left duration-100";

  if (meta.touched && meta.error) {
    containerClasses += " border-brand-tertiary-50 shadow-outline-error";
  } else {
    if (!readonly && focused) {
      containerClasses += " border-input-border-focused shadow-outline";
    } else {
      containerClasses += " border-input-border";
    }
  }

  if (!readonly && focused) {
    labelClasses += " text-brand-primary-50";
  } else {
    labelClasses += " text-label";
  }

  if ((!readonly && focused) || !isNullOrWhiteSpace(meta.value)) {
    labelClasses += " scale-75 -translate-y-1";
  }

  if (readonly) {
    containerClasses += " bg-input-bg-readonly";
    inputClasses += " bg-input-bg-readonly";
  }

  return (
    <InputContainer additionalClassName={containerClasses} meta={meta}>
      {label && (
        <label htmlFor={name} className={labelClasses}>
          {label}
        </label>
      )}
      <textarea
        {...field}
        autoComplete={
          autoComplete || (autoCompleteSupportedFields.includes(name) ? "on" : "off")
        }
        id={name}
        className={inputClasses}
        placeholder={focused ? placeholder : undefined}
        onFocus={onFocus}
        onBlur={onBlur}
        readOnly={readonly}
      />
    </InputContainer>
  );
};

export default TextArea;
